// @flow
import React from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import {LockOutlined, TrendingUpOutlined} from '@material-ui/icons';

import CustomButton from '../../../elements/CustomButton/CustomButton';
import {colors} from '../../../assets/styles/colors';
import {useUser} from '../../auth/utils';
import {useIsFeatureInTier} from '../../../utils/component';
import type {StoreFeature} from '../../../entities/store-feature';
import {GENERIC_MESSAGES, FEATURE_ACCESS_ROUTES} from '../utils/constants';

type Props = {
  feature: StoreFeature,
};

const useStyles = makeStyles(() => ({
  overlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.85)',
    backdropFilter: 'blur(2px)',
    display: 'flex',
    justifyContent: 'center',
    zIndex: 1,
    textAlign: 'center',
  },
  messageContainer: {
    backgroundColor: colors.grey100,
    position: 'sticky',
    top: '20%',
    borderRadius: '1rem',
    alignSelf: 'flex-start',
    padding: '1.5rem',
    maxWidth: 500,
    border: `1px solid ${colors.grey300}`,
    boxShadow: '0 4px 1.5rem rgba(145, 145, 145, 0.25)',
  },
  title: {
    fontSize: 24,
    fontWeight: 700,
    color: colors.grey1000,
    marginBottom: 16,
    textAlign: 'center',
    letterSpacing: '-0.5px',
  },
  message: {
    fontSize: 16,
    color: colors.grey800,
    lineHeight: 1.6,
    marginBottom: 32,
    textAlign: 'center',
    fontWeight: 400,
  },
  upgradeButton: {
    backgroundColor: colors.primary,
    color: 'white',
    fontSize: 16,
    fontWeight: 600,
    textTransform: 'none',
    boxShadow: '0 4px 12px rgba(123, 57, 255, 0.3)',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      backgroundColor: colors.purple4,
      boxShadow: '0 6px 16px rgba(123, 57, 255, 0.4)',
      transform: 'translateY(-1px)',
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: '0 2px 8px rgba(123, 57, 255, 0.3)',
    },
  },
  iconContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: 20,
  },
  icon: {
    fontSize: 48,
    color: colors.primary,
    opacity: 0.8,
  },
  restrictionIcon: {
    fontSize: 48,
    color: colors.grey600,
    opacity: 0.8,
  },
  accessButton: {
    backgroundColor: colors.info,
    color: 'white',
    '&:hover': {
      backgroundColor: '#0082CC',
    },
  },
}));

export default function FeatureAccessOverlay({feature}: Props) {
  const classes = useStyles();
  const user = useUser();
  const hasFeature = useIsFeatureInTier(feature);

  if (!user) {
    return null;
  }

  if (hasFeature) {
    return null;
  }

  const isVendor = user.type === 'vendor-user';
  const message = isVendor
    ? GENERIC_MESSAGES.upselling
    : GENERIC_MESSAGES.restriction;

  const handleButtonClick = () => {
    window.location.href = FEATURE_ACCESS_ROUTES.tiersList;
  };

  return (
    <div className={classes.overlay}>
      <div className={classes.messageContainer}>
        <div className={classes.iconContainer}>
          {isVendor ? (
            <TrendingUpOutlined className={classes.icon} />
          ) : (
            <LockOutlined className={classes.restrictionIcon} />
          )}
        </div>
        <div className={classes.title}>
          No tienes acceso a esta funcionalidad
        </div>
        <div className={classes.message}>{message}</div>
        {isVendor && (
          <CustomButton
            variant="contained"
            className={classes.upgradeButton}
            onClick={handleButtonClick}
            fullWidth
          >
            <span role="img" aria-label="sparkles">
              ✨
            </span>{' '}
            Mejorar Plan
          </CustomButton>
        )}
      </div>
    </div>
  );
}
